import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { LogOut, Shield, MessageCircle, UserCircle2 } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const unread = useUnreadMessages(user);

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const linkBase = "px-3 py-2 text-xs uppercase tracking-[0.18em] font-varsity transition-colors";
  const linkActive = ({ isActive }) =>
    `${linkBase} ${isActive ? "text-[#00E5FF]" : "text-white/70 hover:text-white"}`;

  return (
    <header
      data-testid="site-navbar"
      className="glass-nav sticky top-0 z-40 w-full"
    >
      <div className="mx-auto max-w-7xl flex items-center justify-between px-6 py-3">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img
            src={LOGO_URL}
            alt="Dazzlin' Dymes"
            className="h-12 w-12 object-contain drop-shadow-[0_0_8px_rgba(0,229,255,0.45)]"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-xl text-chrome tracking-wide">DAZZLIN' DYMES</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#00E5FF]">Dance Team</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={linkActive} data-testid="nav-home">Home</NavLink>
          <NavLink to="/events" className={linkActive} data-testid="nav-events">Events</NavLink>
          <NavLink to="/gallery" className={linkActive} data-testid="nav-gallery">Gallery</NavLink>
          <NavLink to="/tuition" className={linkActive} data-testid="nav-tuition">Tuition</NavLink>
          <NavLink to="/shop" className={linkActive} data-testid="nav-shop">Shop</NavLink>
          {(!user || user === false) && (
            <NavLink to="/join" className={linkActive} data-testid="nav-join">Join</NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/dashboard" className={linkActive} data-testid="nav-dashboard">Dashboard</NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/messages" className={linkActive} data-testid="nav-messages">
              <span className="inline-flex items-center gap-1.5 relative">
                <MessageCircle size={12} /> Messages
                {unread > 0 && (
                  <span className="ml-1 min-w-[18px] h-[18px] px-1.5 grid place-items-center rounded-full bg-[#00E5FF] text-black text-[10px] font-bold" data-testid="nav-messages-badge">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </span>
            </NavLink>
          )}
          {user && user !== false && user.role === "admin" && (
            <NavLink to="/admin" className={linkActive} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1"><Shield size={12} /> Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user && user !== false ? (
            <>
              <Link to="/account" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white" data-testid="nav-account-link">
                <UserCircle2 size={14} /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={onLogout} className="btn-ghost !px-4 !py-2" data-testid="nav-logout-btn">
                <LogOut size={14} /> Logout
              </button>
            </>
          ) : user === false ? (
            <>
              <Link to="/login" className="btn-ghost !px-4 !py-2" data-testid="nav-login-btn">Login</Link>
              <Link to="/register" className="btn-electric !px-4 !py-2" data-testid="nav-register-btn">Join</Link>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
